import { forwardRef, Fragment, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, NavLink, useLocation } from 'react-router-dom'
import { useSidebarLinks } from 'hooks/UseSidebarLinks'
import { useUser } from 'hooks/UseUser'
import { useSiteConfig } from 'hooks/UseSiteConfig'
import { DuplicateIcon, MenuIcon } from '@heroicons/react/outline'
import { useActiveFeatureFlags, useCurrentSite } from 'contexts/SiteConfig'
import { Dialog, Transition } from '@headlessui/react'
import { ChevronDownIcon, XIcon } from '@heroicons/react/solid'
import { Logo } from 'ui/components/Logo'
import { Tooltip, TooltipReference, useTooltipState } from 'reakit/Tooltip'
import { Modal } from 'ui/Modal'
import { ResourceListItem } from 'ui/components/ResourceListItem'
import classNames from 'classnames'
import { SidebarContext, useSidebar } from './contexts'
import cookies from 'react-cookies'
import { useWindowWidth } from 'hooks/UseWindowWidth'
import { useGetUserSites } from 'api/Users'
import { Badge } from 'ui/components/Badge'

export const SidebarProvider = ({ children }) => {
  const featureFlags = useActiveFeatureFlags()
  const expandableNav = featureFlags.includes('expandable_nav')
  const [_minimised, _setMinimised] = useState(() => {
    // If the feature flag is on, store the state in a cookie
    if (expandableNav) {
      return cookies.load('sidebarMinimised') === 'true'
    }

    return false
  })

  const [previousPages, setPreviousPages] = useState([])
  const [title, setTitle] = useState(null)
  const width = useWindowWidth()

  useEffect(() => {
    if (expandableNav) {
      _setMinimised(cookies.load('sidebarMinimised') === 'true')
    }
  }, [expandableNav])

  const minimised = useMemo(() => {
    // If the width is less than 768 (md), always return false
    if (width < 768) {
      return false
    }

    return _minimised
  }, [width, _minimised])

  const setMinimised = (state) => {
    _setMinimised(state)
    cookies.save('sidebarMinimised', state, {
      path: '/',
    })
  }

  return (
    <SidebarContext.Provider
      value={{
        minimised,
        setMinimised,
        setTitle,
        title,
        setPreviousPages,
        previousPages,
      }}
    >
      {children}
    </SidebarContext.Provider>
  )
}

const SidebarTooltip = ({ tooltip, label }) => (
  <Tooltip
    {...tooltip}
    className="z-20 text-xs font-medium bg-black bg-opacity-75 rounded-xl p-1 px-2 text-white style-reset"
  >
    {label}
  </Tooltip>
)

const Link = ({ link, pinToBottom }) => {
  const { minimised } = useContext(SidebarContext)
  const { pathname } = useLocation()
  const tooltip = useTooltipState({ placement: 'right' })
  const [expanded, setExpanded] = useState(false)
  const featureFlags = useActiveFeatureFlags()

  useEffect(() => {
    const foundAsChild =
      (link.childLinks ?? []).find((childLink) => {
        return pathname.startsWith(childLink.to)
      }) !== undefined

    setExpanded(minimised !== true && (pathname.startsWith(link.to) || foundAsChild))
  }, [pathname, minimised])

  const handleClick = (event) => {
    setExpanded(!expanded)
    event.preventDefault()
  }

  return (
    <>
      <TooltipReference
        {...tooltip}
        as={NavLink}
        to={link.to}
        className={classNames(
          'text-grape-300 dark:text-gray-300 hover:bg-grape-700 dark:hover:bg-gray-800 w-full group flex items-center p-2 font-medium rounded-xl focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white',
          {
            'flex-col text-xs text-center': minimised,
            'text-sm': !minimised,
            'mt-auto': pinToBottom,
          }
        )}
        activeClassName="nav-link-active"
        title={link.title}
        exact
      >
        <LinkIcon link={link} minimised={minimised} />
        <span className={classNames('truncate', { 'sr-only': minimised })}>{link.title}</span>
        {!minimised && link.childLinks?.length > 0 && featureFlags.includes('expandable_nav') && (
          <span
            className="ml-auto p-1 hover:bg-grape-400 hover:text-grape-800 rounded-xl"
            onClick={handleClick}
          >
            <ChevronDownIcon className="w-4 h-4 " />
          </span>
        )}

        {(link.isNew || link.isBeta) && !minimised && (
          <div className="flex-1 text-right dark">
            {link.isNew && <Badge label="New!" variant="info" />}
            {link.isBeta && <Badge label="Beta" variant="warning" />}
          </div>
        )}
      </TooltipReference>

      {minimised && <SidebarTooltip tooltip={tooltip} label={link.title} />}

      {featureFlags.includes('expandable_nav') && (
        <Transition
          show={expanded}
          enter="transition-all ease-in-out duration-100"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-all ease-in-out duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          {(link.childLinks ?? []).map((childLink) => {
            const isActive = pathname.startsWith(childLink.to)

            return (
              <NavLink
                key={childLink.to}
                to={childLink.to}
                className={classNames(
                  'w-full group flex items-center py-2 pl-11 pr-2 font-medium rounded-xl focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white',
                  {
                    'flex-col text-xs text-center': minimised,
                    'text-sm': !minimised,
                    'mt-auto': pinToBottom,
                    'text-grape-300 dark:text-gray-300 hover:bg-grape-700 dark:hover:bg-gray-800':
                      !isActive,
                    'bg-coral bg-opacity-25 hover:bg-coral hover:bg-opacity-25 text-white dark:bg-gray-800':
                      isActive,
                  }
                )}
              >
                {childLink.title}
              </NavLink>
            )
          })}
        </Transition>
      )}
    </>
  )
}

const LinkIcon = ({ link, minimised }) => {
  if (link.iconExtra) {
    return (
      <div className="nav-link-svg text-grape-400 group-hover:text-white group-focus:text-white flex-shrink-0 flex relative">
        <LinkHeroIcon icon={link.heroIcon} minimised={minimised} />
        {link.iconExtra && <link.iconExtra />}
      </div>
    )
  }

  return <LinkHeroIcon icon={link.heroIcon} minimised={minimised} />
}

const LinkHeroIcon = ({ icon: Icon, minimised }) => (
  <Icon
    className={`nav-link-svg text-grape-400 group-hover:text-white group-focus:text-white flex-shrink-0 h-6 w-6 ${
      !minimised && 'mr-3'
    }`}
    aria-hidden="true"
  />
)

const CustomSiteSwitcherButton = forwardRef<any, any>((props, ref) => (
  <button ref={ref} className={props.className} onClick={props.onClick}>
    {props.leadingIcon &&
      props.leadingIcon({
        className: 'w-6 h-6',
      })}
    {!props.minimised && <span className="ml-3">{props.label}</span>}
  </button>
))

const TooltipReferenceListButton = ({ ...rest }) => (
  <TooltipReference as={CustomSiteSwitcherButton} {...rest} />
)

const SiteSwitcherOverlay = () => {
  const [opening, setOpening] = useState(false)
  const userId = useUser()?.sub
  const { data: { data: sites = [] } = {} } = useGetUserSites(userId, {
    cacheTime: Infinity,
    staleTime: 60 * 60 * 1000, // 60 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })
  const site = useCurrentSite()
  const { setActiveSiteId } = useSiteConfig()
  const { minimised } = useContext(SidebarContext)
  const tooltip = useTooltipState({ placement: 'right' })

  if (sites.length === 1) {
    return null
  }

  return (
    <>
      <TooltipReferenceListButton
        minimised={minimised}
        label={site.name}
        leadingIcon={DuplicateIcon}
        variant="primary"
        fullWidth
        className={`${
          opening
            ? 'bg-grape-600 dark:bg-gray-800 text-white'
            : 'bg-grape-700 dark:bg-gray-800 text-grape-300 dark:text-gray-300'
        } hover:bg-grape-600 dark:hover:bg-gray-700 w-full group flex items-center p-2 text-sm font-medium rounded-xl focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white ${
          minimised && 'flex-col text-center text-xs'
        }`}
        {...tooltip}
        onClick={() => setOpening(true)}
      />

      {minimised && <SidebarTooltip tooltip={tooltip} label={site.name} />}

      <Modal isOpen={opening} title="Select site" onClose={() => setOpening(false)} styleReset>
        {sites.map((site) => (
          <ResourceListItem
            key={site.id}
            title={site.name}
            showDisclosureArrow
            onClick={() => {
              setActiveSiteId(site.id)
              setOpening(false)
            }}
          />
        ))}
      </Modal>
    </>
  )
}

const SidebarInner = () => {
  const { minimised } = useContext(SidebarContext)
  const history = useHistory()
  const links = useSidebarLinks()

  return (
    <div className="flex-1 flex flex-col h-screen bg-purple dark:bg-gray-900 max-h-[-webkit-fill-available]">
      <div className="flex-1 flex flex-col pb-4 overflow-y-auto">
        <div className="px-2 pt-3 space-y-4 sticky top-0 bg-purple dark:bg-gray-900 bg-opacity-80 backdrop-filter backdrop-blur">
          <div
            className={classNames(
              'mb-4 mx-2 h-10 flex cursor-pointer',
              minimised ? 'justify-center' : ''
            )}
            onClick={() => history.push('/')}
          >
            <Logo variant="dark" width={minimised ? 30 : 130} icon={!!minimised} />
          </div>
          {import.meta.env.VITE_APP_PROJECT_ENVIRONMENT !== 'production' && (
            <div
              className="truncate text-white text-xs font-bold uppercase bg-violet px-2 py-1 bottom-0 rounded-md"
              title={import.meta.env.VITE_APP_PROJECT_ENVIRONMENT.toUpperCase()}
            >
              {import.meta.env.VITE_APP_PROJECT_ENVIRONMENT}
            </div>
          )}
          <div className="mb-1">
            <SiteSwitcherOverlay />
          </div>
        </div>
        <nav className="flex-1 flex flex-col px-2 gap-y-1">
          {links.map((link, i) => (
            <Link link={link} key={link.to} pinToBottom={link.to === '/settings'} />
          ))}
        </nav>
      </div>
    </div>
  )
}

export const MobileSidebar = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  let closeButtonRef = useRef(null)
  const { pathname } = useLocation()

  useEffect(() => {
    // The pathname has changed, so close the mobile menu.
    setSidebarOpen(false)
  }, [pathname])

  return (
    <>
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog
          initialFocus={closeButtonRef}
          as="div"
          open={true}
          className="fixed inset-0 flex z-40 style-reset"
          onClose={() => setSidebarOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-600 bg-opacity-75" />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <div className="relative flex-1 flex flex-col max-w-xs w-full bg-grape-700">
              <Transition.Child
                as={Fragment}
                enter="ease-in-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in-out duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="absolute top-0 right-0 -mr-12 pt-2">
                  <button
                    type="button"
                    className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                    onClick={() => setSidebarOpen(false)}
                    ref={closeButtonRef}
                  >
                    <span className="sr-only">Close sidebar</span>
                    <XIcon className="h-6 w-6 text-white" aria-hidden="true" />
                  </button>
                </div>
              </Transition.Child>
              <div className="flex-shrink-0 flex items-center">
                <SidebarInner />
              </div>
            </div>
          </Transition.Child>
        </Dialog>
      </Transition.Root>
      <button
        className="p-2 rounded-full text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-coral"
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        <span className="sr-only">Open sidebar</span>
        <MenuIcon className="w-6 h-6" aria-hidden="true" />
      </button>
    </>
  )
}

export const Sidebar = () => {
  const { minimised } = useSidebar()

  return (
    <>
      {/* Desktop sidebar */}
      <div className="hidden md:flex md:flex-shrink-0 sticky top-0 style-reset">
        <div
          className={`flex flex-col border-r border-gray-200 dark:border-transparent transition-width ${
            minimised ? 'w-20' : 'w-60'
          }`}
        >
          <SidebarInner />
        </div>
      </div>
    </>
  )
}
